import React, { useId, useRef } from 'react';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { cn } from '@rocco/utils/cn';
import { Button } from '@rocco/components/button';
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from '@rocco/components/form';
import { Input } from '@rocco/components/input';
import { Separator } from '@rocco/components/separator';
import {
    Tabs,
    TabsContent,
    TabsList,
    TabsTrigger,
} from '@rocco/components/tabs';
import type { Conversation, PartialConversation } from '../../../types';
import { useUpdateConversation } from '../../../hooks/use-update-conversation';
import { ChatBoxSettingsMembers } from './ChatBoxSettingsMembers';

const settingsFormSchema = z.object({
    title: z
        .string()
        .trim()
        .max(100, { message: 'Maximum 100 characters' })
        .transform((value) => value.replace(/\s+/, ' ')),
});

/**
 * The settings interface for a group chat in a chat box. Contains the group
 * chat settings, and the members of the group chat.
 * @see {@link ChatBox}
 */
export function ChatBoxSettings({
    className,
    conversation,
    onClose,
}: {
    className?: string;
    conversation: Extract<
        Conversation | PartialConversation,
        { __typename: 'DirectConversation' }
    >;
    onClose?: () => void;
}) {
    const { isSubmitting, updateConversation } = useUpdateConversation({
        conversationID: Number(conversation.ID),
        onSuccess: (conversation) => {
            settingsForm.reset({ title: conversation.Name ?? '' });
            onClose?.();
        },
    });
    const settingsFormReactId = useId();
    const settingsFormId = `chat-box-settings-${settingsFormReactId}`;
    const settingsFormRef = useRef<HTMLFormElement>(null);
    const settingsForm = useForm<z.infer<typeof settingsFormSchema>>({
        resolver: zodResolver(settingsFormSchema),
        mode: 'onChange',
        defaultValues: {
            title: conversation.Name ?? '',
        },
        disabled: isSubmitting,
    });

    function onSubmit(values: z.infer<typeof settingsFormSchema>) {
        updateConversation(values.title);
    }

    return (
        <Tabs defaultValue="settings" className={cn('gap-0', className)}>
            <TabsList className="h-[36px] bg-transparent p-0">
                <ChatBoxSettingsTabsTrigger value="settings">
                    Settings
                </ChatBoxSettingsTabsTrigger>
                <ChatBoxSettingsTabsTrigger value="members">
                    Members
                </ChatBoxSettingsTabsTrigger>
            </TabsList>
            <Separator className="mb-[32px] bg-white" />
            <TabsContent value="settings">
                <Form {...settingsForm}>
                    <form
                        ref={settingsFormRef}
                        id={settingsFormId}
                        className="flex size-full flex-col gap-[8px]"
                        onSubmit={settingsForm.handleSubmit(onSubmit)}
                    >
                        <div className="min-h-0 flex-1 space-y-[16px]">
                            <FormField
                                name="title"
                                control={settingsForm.control}
                                render={({ field }) => (
                                    <FormItem className="space-y-[8px]">
                                        <FormLabel
                                            className="ml-[4px] text-[14px]"
                                            errorVariant="errorDarker"
                                        >
                                            Conversation title
                                        </FormLabel>
                                        <FormControl>
                                            <Input
                                                className="rounded-[8px] px-[12px] py-[8px] text-[16px] md:!text-[14px]"
                                                color="contrast"
                                                placeholder="Enter a title for this conversation or group"
                                                autoFocus
                                                autoComplete="off"
                                                {...field}
                                            />
                                        </FormControl>
                                        <FormMessage
                                            className="text-[12px]"
                                            variant="darker"
                                        />
                                    </FormItem>
                                )}
                            />
                        </div>
                        <div className="flex items-center justify-end gap-[4px]">
                            <ChatBoxSettingsButton
                                className="hover:bg-white/50"
                                type="button"
                                color="transparent"
                                disabled={settingsForm.formState.disabled}
                                onClick={onClose}
                            >
                                Cancel
                            </ChatBoxSettingsButton>
                            <ChatBoxSettingsButton
                                className="min-w-[120px]"
                                type="submit"
                                disabled={
                                    settingsForm.formState.disabled ||
                                    !settingsForm.formState.isDirty ||
                                    !settingsForm.formState.isValid
                                }
                                isLoading={settingsForm.formState.disabled}
                            >
                                Save & Close
                            </ChatBoxSettingsButton>
                        </div>
                    </form>
                </Form>
            </TabsContent>
            <TabsContent value="members" className="flex flex-col gap-[8px]">
                <ChatBoxSettingsMembers
                    className="min-h-0 flex-1"
                    conversation={conversation}
                />
                <div className="flex items-center justify-end">
                    <ChatBoxSettingsButton
                        className="min-w-[120px]"
                        onClick={onClose}
                    >
                        Save & Close
                    </ChatBoxSettingsButton>
                </div>
            </TabsContent>
        </Tabs>
    );
}

function ChatBoxSettingsTabsTrigger({
    children,
    className,
    ...props
}: React.ComponentProps<typeof TabsTrigger>) {
    return (
        <TabsTrigger
            className={cn(
                'group relative rounded-none bg-transparent px-[8px] py-[4px] text-[12px] text-black/50 data-[state=active]:bg-transparent data-[state=active]:text-black',
                className
            )}
            {...props}
        >
            {children}
            <div className="absolute inset-x-[-1px] bottom-[-2px] hidden h-[2px] bg-black group-data-[state=active]:block"></div>
        </TabsTrigger>
    );
}

function ChatBoxSettingsButton({
    children,
    className,
    ...props
}: React.ComponentProps<typeof Button>) {
    return (
        <Button
            className={cn(
                'px-[16px] py-[8px] text-[14px] leading-tight',
                className
            )}
            color="dark"
            weight="normal"
            {...props}
        >
            {children}
        </Button>
    );
}
